import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { CellKey, ScaleCell, ScaleConfig, Verdict } from 'Types/ExerciseContentTypes';
import type { ScaleProgressCell, ExerciseProgressPoint } from 'Types/ExerciseProgressTypes';
import type { SessionSummary } from 'Phaser/ExerciseSessionMachine/exerciseSessionMachine.types';

/**
 * Exercise-section progress slice (MUS-318, 7c). The client mirror of MUS-314's
 * per-cell frontier store: keyed by exercise, then by cell (`scale:tonic`), each
 * cell holding its pruned frontier of points. This is deliberately NOT a flat
 * tier/level mirror - the progress model is #117's per-cell Pareto frontier.
 *
 * React reacts to the engine's emitted facts (#115): the machine owns state and
 * emits `playthroughCompleted` / `pointEarned` / `sessionCompleted`; this slice
 * and its saga (`Sagas/ExerciseProgress`) observe them and drive render /
 * navigation / feedback. Nothing here drives the machine.
 *
 * Server-authoritative: `pointEarned` marks a write in flight but does NOT mutate
 * the frontier locally. The backend runs domination/prune (it can absorb a clear
 * the client would otherwise show), so the frontier only ever changes from a
 * server read (`progressHydrated`) or the cell a write returns (`clearWriteSucceeded`).
 * That is why there is no optimistic insert and no rollback path.
 *
 * Not redux-persisted (see `Reducers/index.ts`): a persisted mirror of a
 * server-authoritative store invites stale-rehydrate bugs. It hydrates on mount.
 */

/** Canonical cell serialization, e.g. `major:C` (the schema's `cell_key`). */
export const cellKey = (cell: ScaleCell): CellKey => `${cell.scale}:${cell.tonic}`;

/** The last take's feedback fact. 7c only carries it; the green/yellow/red
 *  verdict UI is MUS-320's seam, not built here. */
export interface PlaythroughFeedback {
  cellKey: CellKey;
  accuracy: number;
  verdict: Verdict;
  playthroughIndex: number;
}

export interface ExerciseProgressState {
  /** exercise_id -> cellKey -> the cell and its pruned frontier. */
  byExercise: Record<number, Record<CellKey, ScaleProgressCell>>;
  /** The exercise the open sitting belongs to; the saga needs it to attribute a write. */
  currentExerciseId: number | null;
  /** The backend play_session_id for the open sitting (MUS-459), or null before the
   *  open lands / when it degraded. The saga threads it onto the clear and the two
   *  server-emitted events, and closes the sitting on it. */
  playSessionId: string | null;
  sessionActive: boolean;
  /** A single global write status. Last-write-wins across concurrent clears in one
   *  sitting (a stale 'error' could mask a concurrent success). Acceptable while clears
   *  are effectively serial per sitting; if MUS-459 needs per-write status, key this by
   *  cell/write id then. */
  writeStatus: 'idle' | 'writing' | 'error';
  lastError: string | null;
  /** Feedback seam (render): the most recent take's outcome. */
  lastPlaythrough: PlaythroughFeedback | null;
  /** Navigation seam: set when a sitting closes; the Phase 3 screen shell consumes it. */
  lastCompletedSummary: SessionSummary | null;
}

export const initialExerciseProgressState: ExerciseProgressState = {
  byExercise: {},
  currentExerciseId: null,
  playSessionId: null,
  sessionActive: false,
  writeStatus: 'idle',
  lastError: null,
  lastPlaythrough: null,
  lastCompletedSummary: null,
};

const exerciseProgressSlice = createSlice({
  name: 'exerciseProgress',
  initialState: initialExerciseProgressState,
  reducers: {
    /** A sitting opened. Dispatched by the launcher when the scene starts; carries
     *  the exercise so the saga can attribute earned clears. */
    sessionStarted(state, action: PayloadAction<{ exerciseId: number }>) {
      state.currentExerciseId = action.payload.exerciseId;
      state.playSessionId = null; // a new sitting; the open worker sets it
      state.sessionActive = true;
      state.writeStatus = 'idle';
      state.lastError = null;
      state.lastPlaythrough = null;
      state.lastCompletedSummary = null;
    },

    /** The backend minted the sitting's play_session (MUS-459). The saga's open worker
     *  dispatches this on success; a degraded open never dispatches it, so playSessionId
     *  stays null and the clear/events fall back to no sitting join. */
    playSessionOpened(state, action: PayloadAction<{ playSessionId: string }>) {
      state.playSessionId = action.payload.playSessionId;
    },

    /** A read of the per-cell frontier for one exercise (initial load / re-hydrate).
     *  Server-authoritative: replaces this exercise's cell map wholesale. Today hydrate
     *  fires only at scene start (before any write), so the wholesale replace is safe;
     *  if re-hydrate is ever wired mid-sitting concurrent with an in-flight clear, the
     *  read could momentarily regress the frontier until clearWriteSucceeded re-adds it. */
    progressHydrated(
      state,
      action: PayloadAction<{ exerciseId: number; cells: ScaleProgressCell[] }>,
    ) {
      const { exerciseId, cells } = action.payload;
      const map: Record<CellKey, ScaleProgressCell> = {};
      for (const cell of cells) {
        map[cellKey(cell)] = cell;
      }
      state.byExercise[exerciseId] = map;
    },

    /** The saga asks for a hydrate; caught there. Reducer is a no-op marker so the
     *  intent is a first-class, testable action rather than a bare saga trigger. */
    hydrateRequested(_state, _action: PayloadAction<{ exerciseId: number }>) {
      // handled by the saga (GET); no local state change.
    },

    /** Emitted `playthroughCompleted` - the feedback fact for every take. Does not
     *  touch the frontier (only an earned bar does). */
    playthroughCompleted(
      state,
      action: PayloadAction<{
        cell: ScaleCell;
        config: ScaleConfig;
        accuracy: number;
        verdict: Verdict;
        playthroughIndex: number;
      }>,
    ) {
      const { cell, accuracy, verdict, playthroughIndex } = action.payload;
      state.lastPlaythrough = { cellKey: cellKey(cell), accuracy, verdict, playthroughIndex };
    },

    /** Emitted `pointEarned` - a clearance/mastery bar was met. The write-status
     *  transition is saga-owned (see clearWriteStarted): the saga flips to 'writing'
     *  only after its guards (flag on, active exercise) pass, so a guarded skip can
     *  never leave the indicator stuck at 'writing'. No state change here; the frontier
     *  is NOT mutated (server-authoritative). */
    pointEarned(
      _state,
      _action: PayloadAction<{ cell: ScaleCell; config: ScaleConfig; mastered: boolean }>,
    ) {
      // intentionally empty - the saga observes this action and owns the write path.
    },

    /** The saga decided to POST (guards passed): mark the write in flight. Owning this
     *  transition in the saga, not in `pointEarned`, is what keeps the indicator and the
     *  effect in sync. */
    clearWriteStarted(state) {
      state.writeStatus = 'writing';
      state.lastError = null;
    },

    /** The backend accepted the clear and returned the cell's canonical frontier. */
    clearWriteSucceeded(
      state,
      action: PayloadAction<{ exerciseId: number; cell: ScaleProgressCell }>,
    ) {
      const { exerciseId, cell } = action.payload;
      const forExercise = state.byExercise[exerciseId] ?? {};
      forExercise[cellKey(cell)] = cell;
      state.byExercise[exerciseId] = forExercise;
      state.writeStatus = 'idle';
      state.lastError = null;
    },

    /** The clear write failed. The engine already counted it in-sitting (#128), so
     *  the earned point is not lost to the user's current run; only the durable
     *  mirror missed it, and the next hydrate reconciles. */
    clearWriteFailed(state, action: PayloadAction<{ error: string }>) {
      state.writeStatus = 'error';
      state.lastError = action.payload.error;
    },

    /** Emitted `sessionCompleted` - the sitting closed. Navigation seam. */
    sessionCompleted(state, action: PayloadAction<{ summary: SessionSummary }>) {
      state.sessionActive = false;
      state.lastCompletedSummary = action.payload.summary;
    },

    /** Tear-down: the actor is reusable across sittings, so the surface can reset. */
    sessionUnloaded(state) {
      state.currentExerciseId = null;
      state.playSessionId = null;
      state.sessionActive = false;
      state.writeStatus = 'idle';
      state.lastError = null;
      state.lastPlaythrough = null;
    },
  },
});

export const {
  sessionStarted,
  playSessionOpened,
  progressHydrated,
  hydrateRequested,
  playthroughCompleted,
  pointEarned,
  clearWriteStarted,
  clearWriteSucceeded,
  clearWriteFailed,
  sessionCompleted,
  sessionUnloaded,
} = exerciseProgressSlice.actions;

export const exerciseProgressReducer = exerciseProgressSlice.reducer;

/* ------------------------------------------------------------------ *
 * Selectors. Typed over the minimal slice-root shape rather than the
 * full RootState, so there is no import cycle with `Reducers/index.ts`
 * and they are trivially testable. RootState satisfies this structurally.
 * ------------------------------------------------------------------ */

type WithExerciseProgress = { exerciseProgressReducer: ExerciseProgressState };

export const selectCurrentExerciseId = (s: WithExerciseProgress): number | null =>
  s.exerciseProgressReducer.currentExerciseId;

export const selectPlaySessionId = (s: WithExerciseProgress): string | null =>
  s.exerciseProgressReducer.playSessionId;

export const selectSessionActive = (s: WithExerciseProgress): boolean =>
  s.exerciseProgressReducer.sessionActive;

export const selectWriteStatus = (s: WithExerciseProgress): ExerciseProgressState['writeStatus'] =>
  s.exerciseProgressReducer.writeStatus;

export const selectLastPlaythrough = (s: WithExerciseProgress): PlaythroughFeedback | null =>
  s.exerciseProgressReducer.lastPlaythrough;

export const selectLastCompletedSummary = (s: WithExerciseProgress): SessionSummary | null =>
  s.exerciseProgressReducer.lastCompletedSummary;

/** A single stable, frozen empty map, so the untouched-exercise case does not hand
 *  `useSelector` a fresh `{}` on every store change (reference churn). Frozen so a
 *  stray consumer mutation cannot poison the shared reference. */
const EMPTY_CELL_MAP: Record<CellKey, ScaleProgressCell> = Object.freeze({});

/** The cell map for one exercise (keyed per-cell, not flat). Returns the stored
 *  reference (or the shared empty map on a miss), so it is safe to read from
 *  `useSelector` without reference-churn re-renders. */
export const selectExerciseCells = (
  s: WithExerciseProgress,
  exerciseId: number,
): Record<CellKey, ScaleProgressCell> =>
  s.exerciseProgressReducer.byExercise[exerciseId] ?? EMPTY_CELL_MAP;

/** One (exercise, cell)'s hydrated progress cell, or undefined if untouched. Returns
 *  the stored reference directly (no fresh object on a miss), so it is safe to read
 *  from `useSelector` without reference-churn re-renders. */
export const selectExerciseCell = (
  s: WithExerciseProgress,
  exerciseId: number,
  cell: ScaleCell,
): ScaleProgressCell | undefined =>
  s.exerciseProgressReducer.byExercise[exerciseId]?.[cellKey(cell)];

/** The pruned frontier for one (exercise, cell); undefined if the cell is untouched. */
export const selectCellFrontier = (
  s: WithExerciseProgress,
  exerciseId: number,
  cell: ScaleCell,
): ExerciseProgressPoint[] | undefined =>
  s.exerciseProgressReducer.byExercise[exerciseId]?.[cellKey(cell)]?.frontier;

export default exerciseProgressReducer;
